import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Trophy, Users, Shield, Crown, Compass, ChevronDown } from "lucide-react";

const CATEGORY_LABEL = {
  arena: "Arena Champions",
  dungeon: "Cavern Delvers",
  race: "Race Aces",
  show: "Showcase Winners",
  quest: "Questbearers",
  craft: "Master Crafters",
  gather: "Gatherers",
  trade: "Trade Barons",
  upgrade: "Upgrade Legends",
  merge: "Merge Sages",
  misc: "Wildcards",
};

/**
 * Per-house detail page. Renders the auto-generated Trophy Room for every
 * gameplay category the backend has data for, alongside the pledged Guild
 * list and the house's current rank. Cards that leave the house drop out of
 * the trophy room automatically because `GET /houses/:key` joins on
 * `cards.house_key == :key` at read time.
 */
export default function HouseDetailPage() {
  const { key } = useParams();
  const { data: house, isLoading } = useQuery({
    queryKey: ["house", key],
    queryFn: async () => (await api.get(`/houses/${encodeURIComponent(key)}`)).data,
    staleTime: 20_000,
  });

  if (isLoading || !house) {
    return <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 text-white/50" data-testid="house-detail-loading">Loading house…</div>;
  }

  const tint = house.color || "#94a3b8";
  const s = house.stats || {};
  const trophies = house.trophies || {};
  const catsWithData = Object.entries(trophies).filter(([, rows]) => (rows || []).length > 0);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid={`house-detail-${house.key}`}>
      <Link to="/houses" data-testid="house-detail-back"
        className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.28em] text-white/45 hover:text-cyan-300 mb-3">
        <ArrowLeft className="h-3 w-3" /> All Houses
      </Link>

      {/* Hero */}
      <div
        className="rounded-3xl border p-5 sm:p-8 relative overflow-hidden"
        style={{ borderColor: `${tint}55`, background: `${tint}18` }}
        data-testid="house-detail-hero"
      >
        {house.banner && (
          <img src={house.banner} alt=""
            className="absolute inset-0 h-full w-full object-cover opacity-25 pointer-events-none"
            aria-hidden="true" />
        )}
        <div className="relative flex items-start gap-4">
          <div
            className="h-14 w-14 sm:h-16 sm:w-16 rounded-2xl flex items-center justify-center text-3xl shrink-0"
            style={{ background: `${tint}22`, border: `1px solid ${tint}55` }}
          >
            {house.crest || "🏛"}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-display text-3xl sm:text-4xl text-white tracking-[0.03em] leading-none">
                {house.name}
              </h1>
              <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.24em]"
                style={{ borderColor: tint, color: tint, background: `${tint}12` }}>
                <Crown className="h-3 w-3" /> Rank #{house.rank ?? "—"}
              </span>
            </div>
            <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.28em] text-white/60">{house.motto}</p>
            <p className="mt-2 text-[13.5px] text-white/70 max-w-2xl leading-relaxed">{house.lore}</p>
          </div>
        </div>
        <div className="relative mt-4 grid grid-cols-3 gap-2 font-mono text-[11px]">
          <Stat label="Cards" value={s.card_count?.toLocaleString?.() ?? 0} icon={Shield} tint={tint} testid="house-detail-cards" />
          <Stat label="Guilds" value={s.guild_count?.toLocaleString?.() ?? 0} icon={Users} tint={tint} testid="house-detail-guilds" />
          <Stat label="Points" value={s.total_points?.toLocaleString?.() ?? 0} icon={Trophy} tint={tint} testid="house-detail-points" />
        </div>
        {house.deep_dive && (
          <div className="relative mt-2 grid grid-cols-2 gap-2 font-mono text-[11px]" data-testid="house-detail-deep-dive-stats">
            <Stat label="Dives Total" value={(house.deep_dive.dives_total ?? 0).toLocaleString?.()} icon={Compass} tint={tint} testid="house-detail-dives-total" />
            <Stat label="Deepest Dive" value={house.deep_dive.deepest_dive ?? 0} icon={ChevronDown} tint={tint} testid="house-detail-deepest-dive" />
          </div>
        )}
      </div>

      {/* Trophy Room */}
      <section className="mt-8" data-testid="house-trophy-room">
        <div className="flex items-center gap-2 mb-3">
          <Trophy className="h-4 w-4" style={{ color: tint }} />
          <span className="font-mono text-[10px] uppercase tracking-[0.32em]" style={{ color: tint }}>Trophy Room</span>
        </div>
        {catsWithData.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-black/30 p-6 text-center text-white/50 text-sm">
            No trophies yet — cards pledged here haven&apos;t scored a category-leading finish. Play an Arena bout, run the Gauntlet, or delve the Cavern to seed the leaderboard.
          </div>
        ) : (
          <div className="space-y-4">
            {catsWithData.map(([cat, rows]) => (
              <CategoryShelf key={cat} category={cat} rows={rows} tint={tint} />
            ))}
          </div>
        )}
      </section>

      {/* Guild allegiances */}
      <section className="mt-8" data-testid="house-detail-guilds-panel">
        <Link to={`/guilds?house=${encodeURIComponent(house.key)}`} data-testid="house-browse-guilds" className="inline-flex mb-3 text-sm text-cyan-300 hover:text-cyan-200">Browse all {house.name} Guilds →</Link>
        <div className="flex items-center gap-2 mb-3">
          <Users className="h-4 w-4" style={{ color: tint }} />
          <span className="font-mono text-[10px] uppercase tracking-[0.32em]" style={{ color: tint }}>Pledged Guilds</span>
        </div>
        {(house.guilds || []).length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-black/30 p-4 text-white/50 text-sm">
            No Guilds have pledged to {house.name} yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {house.guilds.map((g) => (
              <Link
                key={g.id}
                to={`/guilds/${g.id}`}
                data-testid={`house-detail-guild-${g.id}`}
                className="rounded-xl border border-white/10 bg-white/[0.04] hover:bg-white/[0.07] transition-colors px-3 py-2 flex items-center gap-2"
              >
                <span className="text-lg leading-none">{g.crest || "🛡"}</span>
                <div className="min-w-0">
                  <div className="text-white text-sm truncate">{g.name}</div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-white/40 truncate">{g.motto || "—"}</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function CategoryShelf({ category, rows, tint }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-black/30 p-3" data-testid={`house-trophy-${category}`}>
      <div className="flex items-baseline justify-between mb-2">
        <div className="font-display text-lg text-white">{CATEGORY_LABEL[category] || category}</div>
        <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-white/40">
          {rows.length} card{rows.length === 1 ? "" : "s"}
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        {rows.slice(0, 5).map((r, i) => (
          <TrophyCard key={r.card_id} row={r} rank={i + 1} tint={tint} />
        ))}
      </div>
    </div>
  );
}

function TrophyCard({ row, rank, tint }) {
  return (
    <Link
      to={`/card/${row.card_id}`}
      data-testid={`trophy-card-${row.card_id}`}
      className="group relative rounded-xl overflow-hidden border border-white/10 bg-black/40 hover:border-white/25 transition-colors"
    >
      <div className="aspect-[3/4] relative overflow-hidden bg-black">
        {row.front_image ? (
          <img src={row.front_image} alt={row.title || "Card"} loading="lazy"
            className="absolute inset-0 h-full w-full object-cover group-hover:scale-[1.03] transition-transform" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-white/30">No art</div>
        )}
        <div className="absolute top-1 left-1 inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.22em]"
          style={{ borderColor: tint, background: `${tint}22`, color: tint }}>
          <Trophy className="h-2.5 w-2.5" /> #{rank}
        </div>
      </div>
      <div className="px-2 py-1.5">
        <div className="text-white text-[12px] truncate" title={row.title}>{row.title || "Untitled"}</div>
        <div className="flex items-center justify-between font-mono text-[9px] uppercase tracking-[0.22em] text-white/50">
          <span>{row.rarity || "—"} · L{row.level ?? 1}</span>
          <span style={{ color: tint }}>{row.points?.toLocaleString?.() ?? 0} pts</span>
        </div>
      </div>
    </Link>
  );
}

function Stat({ label, value, icon: Icon, tint, testid }) {
  return (
    <div className="flex items-center gap-1.5 rounded-md border border-white/10 bg-black/25 px-2 py-1.5" data-testid={testid}>
      <Icon className="h-3.5 w-3.5" style={{ color: tint }} />
      <div className="min-w-0">
        <div className="text-white truncate">{value}</div>
        <div className="text-[9px] uppercase tracking-[0.22em] text-white/40 leading-none">{label}</div>
      </div>
    </div>
  );
}
